// PerformanceMetricsService - Collect and analyze upload performance data
// Phase 3B: Performance Optimization & Monitoring

// Extend interfaces for experimental browser APIs
interface ExtendedMemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  external?: number;
}

interface ExtendedNavigator extends Navigator {
  deviceMemory?: number;
}

interface UploadMetric {
  id: string;
  timestamp: number;
  sessionId: string;
  fileSize: number;
  fileName: string;
  uploadDurationMs: number;
  averageSpeed: number; // bytes per second
  chunkCount: number;
  averageChunkSize: number;
  retryCount: number;
  success: boolean;
  errorType?: string;
  networkQuality?: string;
  memoryUsageStart: number; // MB
  memoryUsageEnd: number; // MB
  memoryPeak: number; // MB
  browserInfo: {
    userAgent: string;
    platform: string;
    availableMemory?: number;
  };
}

interface ChunkMetric {
  uploadId: string;
  chunkIndex: number;
  chunkSize: number;
  uploadTimeMs: number;
  retryAttempts: number;
  speed: number;
  timestamp: number;
}

interface MemorySnapshot {
  timestamp: number;
  heapUsed: number;
  heapTotal: number;
  external: number;
  rss?: number;
  activeUploads: number;
}

interface PerformanceStats {
  totalUploads: number;
  successfulUploads: number;
  failedUploads: number;
  averageUploadSpeed: number;
  averageUploadTime: number;
  totalBytesTransferred: number;
  successRate: number;
  averageRetryCount: number;
  commonErrorTypes: { [key: string]: number };
  memoryUsageTrend: MemorySnapshot[];
  networkQualityDistribution: { [key: string]: number };
}

class PerformanceMetricsService {
  private dbName = "uploadPerformanceMetrics";
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private memorySnapshots: MemorySnapshot[] = [];
  private memoryMonitorInterval: NodeJS.Timeout | null = null;
  private readonly maxMemorySnapshots = 100;
  private readonly maxUploadMetrics = 500;

  constructor() {
    this.initializeDatabase();
    this.startMemoryMonitoring();
  }

  /**
   * Initialize IndexedDB for persistent storage
   */
  private async initializeDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create uploads store
        if (!db.objectStoreNames.contains("uploads")) {
          const uploadStore = db.createObjectStore("uploads", {
            keyPath: "id",
          });
          uploadStore.createIndex("timestamp", "timestamp");
          uploadStore.createIndex("success", "success");
          uploadStore.createIndex("sessionId", "sessionId");
        }

        // Create chunks store
        if (!db.objectStoreNames.contains("chunks")) {
          const chunkStore = db.createObjectStore("chunks", {
            autoIncrement: true,
          });
          chunkStore.createIndex("uploadId", "uploadId");
          chunkStore.createIndex("timestamp", "timestamp");
        }

        // Create memory snapshots store
        if (!db.objectStoreNames.contains("memorySnapshots")) {
          const memoryStore = db.createObjectStore("memorySnapshots", {
            autoIncrement: true,
          });
          memoryStore.createIndex("timestamp", "timestamp");
        }
      };
    });
  }

  /**
   * Start continuous memory monitoring
   */
  private startMemoryMonitoring(): void {
    // Only monitor if performance.memory is available
    if (typeof performance !== "undefined" && performance.memory) {
      this.memoryMonitorInterval = setInterval(() => {
        this.captureMemorySnapshot();
      }, 5000); // Every 5 seconds
    }
  }

  /**
   * Capture current memory snapshot
   */
  private captureMemorySnapshot(): void {
    if (typeof performance === "undefined" || !performance.memory) return;

    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      heapUsed: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024), // MB
      heapTotal: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024), // MB
      external: Math.round(
        ((performance.memory as ExtendedMemoryInfo).external || 0) /
          1024 /
          1024,
      ), // MB
      activeUploads: 0, // Will be updated by external components
    };

    this.memorySnapshots.push(snapshot);

    // Trim old snapshots
    if (this.memorySnapshots.length > this.maxMemorySnapshots) {
      this.memorySnapshots.shift();
    }

    // Store in IndexedDB
    this.storeMemorySnapshot(snapshot);
  }

  /**
   * Record upload start
   */
  recordUploadStart(
    sessionId: string,
    fileName: string,
    fileSize: number,
  ): string {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Store initial state (will be updated on completion)
    const _metric: Partial<UploadMetric> = {
      id: uploadId,
      sessionId,
      fileName,
      fileSize,
      timestamp: Date.now(),
      memoryUsageStart: this.getCurrentMemoryUsage(),
      browserInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        availableMemory:
          (navigator as ExtendedNavigator).deviceMemory || undefined,
      },
    };

    return uploadId;
  }

  /**
   * Record upload completion
   */
  async recordUploadComplete(
    uploadId: string,
    success: boolean,
    uploadDurationMs: number,
    averageSpeed: number,
    chunkCount: number,
    averageChunkSize: number,
    retryCount: number,
    errorType?: string,
    networkQuality?: string,
  ): Promise<void> {
    const metric: UploadMetric = {
      id: uploadId,
      timestamp: Date.now(),
      sessionId: "", // Will be filled from stored data
      fileSize: 0, // Will be filled from stored data
      fileName: "", // Will be filled from stored data
      uploadDurationMs,
      averageSpeed,
      chunkCount,
      averageChunkSize,
      retryCount,
      success,
      errorType,
      networkQuality,
      memoryUsageStart: 0, // Will be filled from stored data
      memoryUsageEnd: this.getCurrentMemoryUsage(),
      memoryPeak: Math.max(
        ...this.memorySnapshots.slice(-10).map((s) => s.heapUsed),
      ),
      browserInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        availableMemory:
          (navigator as ExtendedNavigator).deviceMemory || undefined,
      },
    };

    await this.storeUploadMetric(metric);
  }

  /**
   * Record chunk upload metric
   */
  async recordChunkMetric(chunkMetric: ChunkMetric): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(["chunks"], "readwrite");
    const store = transaction.objectStore("chunks");

    await new Promise<void>((resolve, reject) => {
      const request = store.add(chunkMetric);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get current memory usage in MB
   */
  private getCurrentMemoryUsage(): number {
    if (typeof performance === "undefined" || !performance.memory) return 0;
    return Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
  }

  /**
   * Store upload metric in IndexedDB
   */
  private async storeUploadMetric(metric: UploadMetric): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(["uploads"], "readwrite");
    const store = transaction.objectStore("uploads");

    return new Promise((resolve, reject) => {
      const request = store.put(metric);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Store memory snapshot in IndexedDB
   */
  private async storeMemorySnapshot(snapshot: MemorySnapshot): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(["memorySnapshots"], "readwrite");
    const store = transaction.objectStore("memorySnapshots");

    return new Promise((resolve, reject) => {
      const request = store.add(snapshot);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get performance statistics for a time period
   */
  async getPerformanceStats(
    timeRangeMs: number = 24 * 60 * 60 * 1000,
  ): Promise<PerformanceStats> {
    if (!this.db) {
      return this.getEmptyStats();
    }

    const cutoffTime = Date.now() - timeRangeMs;
    const uploads = await this.getUploadsSince(cutoffTime);
    const memorySnapshots = await this.getMemorySnapshotsSince(cutoffTime);

    const totalUploads = uploads.length;
    const successfulUploads = uploads.filter((u) => u.success).length;
    const failedUploads = totalUploads - successfulUploads;

    const successRate =
      totalUploads > 0 ? (successfulUploads / totalUploads) * 100 : 0;

    const totalBytesTransferred = uploads.reduce(
      (sum, u) => sum + u.fileSize,
      0,
    );
    const totalUploadTime = uploads.reduce(
      (sum, u) => sum + u.uploadDurationMs,
      0,
    );
    const totalSpeed = uploads.reduce((sum, u) => sum + u.averageSpeed, 0);

    const averageUploadSpeed = totalUploads > 0 ? totalSpeed / totalUploads : 0;
    const averageUploadTime =
      totalUploads > 0 ? totalUploadTime / totalUploads : 0;
    const averageRetryCount =
      totalUploads > 0
        ? uploads.reduce((sum, u) => sum + u.retryCount, 0) / totalUploads
        : 0;

    // Error types analysis
    const commonErrorTypes: { [key: string]: number } = {};
    uploads
      .filter((u) => !u.success && u.errorType)
      .forEach((u) => {
        commonErrorTypes[u.errorType!] =
          (commonErrorTypes[u.errorType!] || 0) + 1;
      });

    // Network quality distribution
    const networkQualityDistribution: { [key: string]: number } = {};
    uploads
      .filter((u) => u.networkQuality)
      .forEach((u) => {
        networkQualityDistribution[u.networkQuality!] =
          (networkQualityDistribution[u.networkQuality!] || 0) + 1;
      });

    return {
      totalUploads,
      successfulUploads,
      failedUploads,
      averageUploadSpeed,
      averageUploadTime,
      totalBytesTransferred,
      successRate,
      averageRetryCount,
      commonErrorTypes,
      memoryUsageTrend: memorySnapshots.slice(-20), // Last 20 snapshots
      networkQualityDistribution,
    };
  }

  /**
   * Get recent memory snapshots
   */
  getRecentMemorySnapshots(count: number = 20): MemorySnapshot[] {
    return this.memorySnapshots.slice(-count);
  }

  /**
   * Update active upload count for memory monitoring
   */
  updateActiveUploadCount(count: number): void {
    if (this.memorySnapshots.length > 0) {
      this.memorySnapshots[this.memorySnapshots.length - 1].activeUploads =
        count;
    }
  }

  /**
   * Export metrics data for server sync
   */
  async exportMetricsData(): Promise<{
    uploads: UploadMetric[];
    memorySnapshots: MemorySnapshot[];
    stats: PerformanceStats;
  }> {
    const stats = await this.getPerformanceStats();
    const uploads = await this.getUploadsSince(
      Date.now() - 24 * 60 * 60 * 1000,
    );
    const memorySnapshots = await this.getMemorySnapshotsSince(
      Date.now() - 60 * 60 * 1000,
    ); // Last hour

    return { uploads, memorySnapshots, stats };
  }

  /**
   * Clear old metrics data
   */
  async cleanupOldMetrics(
    olderThanMs: number = 7 * 24 * 60 * 60 * 1000,
  ): Promise<void> {
    if (!this.db) return;

    const cutoffTime = Date.now() - olderThanMs;

    // Clean uploads
    const uploadTransaction = this.db.transaction(["uploads"], "readwrite");
    const uploadStore = uploadTransaction.objectStore("uploads");
    const uploadIndex = uploadStore.index("timestamp");

    const uploadRequest = uploadIndex.openCursor(
      IDBKeyRange.upperBound(cutoffTime),
    );
    uploadRequest.onsuccess = () => {
      const cursor = uploadRequest.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Clean memory snapshots
    const memoryTransaction = this.db.transaction(
      ["memorySnapshots"],
      "readwrite",
    );
    const memoryStore = memoryTransaction.objectStore("memorySnapshots");
    const memoryIndex = memoryStore.index("timestamp");

    const memoryRequest = memoryIndex.openCursor(
      IDBKeyRange.upperBound(cutoffTime),
    );
    memoryRequest.onsuccess = () => {
      const cursor = memoryRequest.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  }

  /**
   * Helper methods for database queries
   */
  private async getUploadsSince(timestamp: number): Promise<UploadMetric[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["uploads"], "readonly");
      const store = transaction.objectStore("uploads");
      const index = store.index("timestamp");
      const request = index.getAll(IDBKeyRange.lowerBound(timestamp));

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  private async getMemorySnapshotsSince(
    timestamp: number,
  ): Promise<MemorySnapshot[]> {
    if (!this.db)
      return this.memorySnapshots.filter((s) => s.timestamp >= timestamp);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(["memorySnapshots"], "readonly");
      const store = transaction.objectStore("memorySnapshots");
      const index = store.index("timestamp");
      const request = index.getAll(IDBKeyRange.lowerBound(timestamp));

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  private getEmptyStats(): PerformanceStats {
    return {
      totalUploads: 0,
      successfulUploads: 0,
      failedUploads: 0,
      averageUploadSpeed: 0,
      averageUploadTime: 0,
      totalBytesTransferred: 0,
      successRate: 0,
      averageRetryCount: 0,
      commonErrorTypes: {},
      memoryUsageTrend: [],
      networkQualityDistribution: {},
    };
  }

  /**
   * Cleanup on service destruction
   */
  destroy(): void {
    if (this.memoryMonitorInterval) {
      clearInterval(this.memoryMonitorInterval);
    }

    if (this.db) {
      this.db.close();
    }
  }
}

// Export singleton instance
export const performanceMetricsService = new PerformanceMetricsService();

export type { UploadMetric, ChunkMetric, MemorySnapshot, PerformanceStats };
